/* eslint-disable max-len */
/* eslint react/no-unused-state:0 */
import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { PreviewBar as defaultPreviewBar } from '@bingads-webui-react/notification-preview-bar';
import { NotificationQueue } from '@bingads-webui-react/notification-queue';
import { Popover } from '@bingads-webui-react/popover';
import { NotificationResources } from '@bingads-webui/notification-service';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import {
  Dialog, DialogFooter, DialogType, PrimaryButton, DefaultButton,
} from '@fluentui/react';
import { Trigger } from './trigger';
import {
  isContextChanged, customKeepOpen,
  logError, validateContext, sortNotifications, getPreviewItems,
  wrapActionForUINext, getPreviewNotifications,
} from './utils';
import { defineResources } from './define-resources';
import {
  scenarioNames, notificationLogKeys, severityOrder, requestStates, actionCallbackTypes,
  previewBarStates, noCacheResponseRequestHeader, notificationSources, previewBarDismissedValue,
  whatsNewId, whatsNewHelpQuery, whatsNewHelpClass, whatsNewSeverity,
} from './constants';
import { getNews } from './whats-new';

export class UnifiedNotificationQueue extends React.Component {
  static getDerivedStateFromProps(props, state) {
    let newState = {};
    if (isContextChanged(props.context, state.context)) {
      props.logger.startProcess(scenarioNames.TriggerShow);
      props.logger.startProcess(scenarioNames.PreviewBarShow);
      props.logger.startProcess(scenarioNames.QueueDisplayShow);

      newState = _.extend({
        // Context
        context: props.context,
        contextChanged: true,
        contextValid: validateContext(props.context),

        // trigger
        showTrigger: false,
        triggerProcessLogged: false,

        // Preview bar
        previewBarProcessLogged: false,

        // Queue
        queueDisplayProcessLogged: false,
        isQueueOpen: false,

        // MT notifications data
        MTNotificationsErrLoaded: false,
        MTNotificationsLoaded: false,
        MTNotifications: [],
        MTNotificationsRequestState: requestStates.notStart,

      }, state.previewBarState !== previewBarStates.dismissed && {
        previewBarState: previewBarStates.notShown,
      });
    }

    if (props.externalNotifications.length > 0) {
      const dialogProps = _.reduce(props.externalNotifications, (result, item) => {
        if (item.id && item.extraOptions && item.extraOptions.dialogProps) {
          // eslint-disable-next-line no-param-reassign
          result[item.id] = item.extraOptions.dialogProps;
        }
        return result;
      }, {});

      newState = _.extend(newState, {
        showTriggerFromExternal: true,
        dialogProps,
      });
    }

    return newState;
  }

  constructor(props) {
    super(props);

    props.logger.startProcess(scenarioNames.TriggerShow);
    props.logger.startProcess(scenarioNames.PreviewBarShow);
    props.logger.startProcess(scenarioNames.QueueDisplayShow);

    // define notification store resources
    defineResources(props.ccBaseUrl);

    this.unmounted = false;
    this.headerBasic = {
      title: props.i18n.getString(_TL_('Notifications')),
    };
    this.footer = {
      text: props.i18n.getString(_TL_('Dismiss all')),
      action: this.dismissAll,
    };
    this.emptyText = props.i18n.getString(_TL_('Nothing to see here.'));
    this.actionCallbackMapping = {
      [actionCallbackTypes.closeQueue]: this.closeQueue,
      [actionCallbackTypes.closeQueueOnAccountsSummaryPage]: this.closeQueueOnAccountsSummaryPage,
      [actionCallbackTypes.openDialog]: this.openDialog,
    };

    this.actionCallback = (callbackType, notificationId) => {
      if (this.actionCallbackMapping[callbackType]) {
        this.actionCallbackMapping[callbackType](notificationId);
      }
    };

    this.shouldWrapAction = props.isUINext && !!props.getPathBySiteMapId && !!props.goTargetPage;

    this.whatsNewNotification = {
      severity: whatsNewSeverity,
      id: whatsNewId,
      title: props.i18n.getString(_TL_("What's new")),
      message: props.i18n.getString(_TL_('We’ve got something shiny and new for you in Microsoft Advertising!')),
      actionList: [{
        text: props.i18n.getString(_TL_('Learn more')),
        extraClassNames: [whatsNewHelpClass],
        extraAttrs: { query: props.whatsNewHelpQuery },
        callbackType: actionCallbackTypes.closeQueue,
      }],
      dismissAction: this.dismissWhatsNewNotification,
      notificationSource: notificationSources.whatsNew,
    };

    this.isPreviewBarFilteringEnabled =
      _.result(props.permissions, ['dynamic', 'PreviewBarFiltering'], false);

    this.state = {
      // Context
      context: props.context,
      contextChanged: false,
      contextValid: validateContext(props.context),

      // trigger
      showTrigger: false,
      showTriggerFromExternal: props.externalNotifications.length > 0,
      triggerMarked: false,
      triggerProcessLogged: false,

      // Preview bar
      previewBarProcessLogged: false,
      previewBarState: previewBarStates.notShown,

      // Queue
      queueDisplayProcessLogged: false,
      isQueueOpen: false,
      showViewAll: false,

      // MT notifications data
      MTNotificationsErrLoaded: false,
      MTNotificationsLoaded: false,
      MTNotifications: [],
      MTNotificationsRequestState: requestStates.notStart,

      // What's new data
      whatsNewData: {},
      whatsNewNotifications: [],
      whatsNewDismissed: false,
      whatsNewRequestState: requestStates.notStart,

      // Dialog
      dialogProps: {},
      notificationId: null,
      isDialogOpen: false,

      unavailableNotifications: [],
    };
    this.focusLockRef = React.createRef();
  }

  // auto-load preview bar data
  componentDidMount() {
    this.fetchPreviewData();
  }

  componentDidUpdate() {
    if (!this.state.showTrigger) {
      this.fetchPreviewData();
    }
    if (this.state.showTrigger || this.state.showTriggerFromExternal) {
      if (!this.state.triggerMarked) {
        this.logTriggerMarker();
      }
      if (!this.state.triggerProcessLogged) {
        this.logTriggerFirstRenderedProcess();
      }
      if (!this.state.previewBarProcessLogged && this.showPreviewBar) {
        this.logPreviewBarFirstRenderedProcess();
      }
      if (!this.state.queueDisplayProcessLogged && this.showQueueDisplay) {
        this.logQueueDisplayFirstRenderedProcess();
      }
    }
    this.props.setIsActive(this.totalCount > 0);
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  get header() {
    const viewAllLink = this.props.generateViewAllLink(this.props, this.state);
    const header = {
      ...this.headerBasic,
      // TODO: remove the fallback for string after all updated to object
      ..._.isObject(viewAllLink) ? viewAllLink : { href: viewAllLink },
      action: this.closeQueueOnAccountsSummaryPage,
      text: this.state.showViewAll ? this.props.i18n.getString(_TL_('View all')) : null,
      isJumpOut: this.props.isUINext,
    };
    return this.shouldWrapAction ?
      wrapActionForUINext(header, this.props.getPathBySiteMapId, this.props.goTargetPage) :
      header;
  }

  // cid & aid are needed because sometimes the values in this.props.context
  // doesn't match the real ones, log it for further investigation
  get contextLogContent() {
    const {
      context: {
        cid, aid, uid, campaignId,
      },
    } = this.props;

    return `cid=${cid}, aid=${aid}, uid=${uid}, cmpid=${campaignId}`;
  }

  get allNotifications() {
    return _.union(
      this.props.externalNotifications,
      this.state.MTNotifications,
      this.state.whatsNewNotifications
    );
  }

  get orderedNotifications() {
    return sortNotifications(this.allNotifications.filter(n => !_.contains(this.state.unavailableNotifications, n.id)));
  }

  get previewNotifications() {
    return this.getPreviewNotifications(this.orderedNotifications);
  }

  get totalCount() {
    return this.state.MTNotifications.length
      + this.state.whatsNewNotifications.length
      + this.props.externalNotifications.length;
  }

  get showPreviewBar() {
    return this.getShowPreviewBar(this.previewNotifications);
  }

  get showQueueDisplay() {
    return (this.state.MTNotificationsLoaded || this.state.MTNotificationsErrLoaded) &&
      (this.popover && this.popover.state.open) &&
      this.allNotifications.length > 0;
  }

  formatMTNotification = notification => ({
    ...notification,
    ...this.shouldWrapAction && {
      actionList: _.map(
        notification.actionList,
        action => wrapActionForUINext(
          action,
          this.props.getPathBySiteMapId,
          this.props.goTargetPage
        )
      ),
    },
    dismissAction: () => this.dismissMTNotification(notification),
  });

  // Generate the log message with context
  logContent = (content = '') => `Context: ${this.contextLogContent}; Content: ${_.isString(content) ? content : JSON.stringify(content)};`;

  logNotificationObj = ({
    total,
    notifications,
  }) => ({
    total,
    notifications: _.map(
      notifications,
      notification => _.pick(notification, notificationLogKeys)
    ),
  });

  endProcessWithCount = scenarioName =>
    () => this.props.logger.endProcess(scenarioName, this.allNotifications.length.toString());

  logError = (scenarioName, error) => logError({
    scenarioName,
    error,
    logContent: this.logContent,
    logger: this.props.logger,
  });

  logTriggerMarker = () => {
    this.props.logger.mark(this.state.contextChanged ?
      scenarioNames.TriggerShowContextChanged : scenarioNames.TriggerShow);
    this.setState({ triggerMarked: true });
  };

  logTriggerFirstRenderedProcess = () => {
    this.props.logger.endProcess(scenarioNames.TriggerShow);
    this.setState({ triggerProcessLogged: true });
  };

  logPreviewBarFirstRenderedProcess = () => {
    const notifications = this.previewNotifications;
    this.props.logger.endProcess(scenarioNames.PreviewBarShow);
    this.props.logger.trace(scenarioNames.PreviewBarRenderNotifications, this.logContent(this.logNotificationObj({ total: notifications.length, notifications })));
    this.setState({ previewBarProcessLogged: true });
  };

  logQueueDisplayFirstRenderedProcess = () => {
    this.props.logger.endProcess(scenarioNames.QueueDisplayShow);
    this.setState({ queueDisplayProcessLogged: true });
  };

  fetchPreviewData() {
    this.fetchMTNotifications(scenarioNames.PreviewBarLoad);
    if (this.props.baseHelpUrl) {
      this.fetchWhatsNewNotification();
    }
  }

  MTNotificationDismissQuery = (dismissNotifications) => {
    const notifications = _.isEmpty(dismissNotifications) ?
      this.state.MTNotifications : dismissNotifications;
    const dismissIds = [];
    const uiDeliveryIds = [];
    const notificationTypeIds = [];
    const opportunityNotifications = [];
    const notificationDeliveryIds = [];
    const notificationTypeIdsForLog = [];
    const addOneNotificationDimissQuery = ({
      item,
      addIndividualQuery = () => uiDeliveryIds.push(item.uiDeliveryId),
      addConsolidatedQuery = () => notificationTypeIds.push(item.notificationTypeId),
    }) => {
      if (item.uiDeliveryId) { // individual notification
        addIndividualQuery();
      } else { // consolidated notification
        addConsolidatedQuery();
      }
    };

    _.each(notifications, (item) => {
      dismissIds.push(item.id);
      switch (item.notificationSource) {
        case notificationSources.adInsightOpportunity:
          opportunityNotifications.push(_.pick(item.opportunityNotification, 'AccountId', 'CampaignId', 'OpportunityTypeId'));
          break;
        case notificationSources.recommendation:
          addOneNotificationDimissQuery({
            item,
            addIndividualQuery: () => notificationDeliveryIds.push({
              deliveryId: item.uiDeliveryId,
              notificationTypeId: item.notificationTypeId,
              notificationSource: item.notificationSource,
            }),
          });
          break;
        default:
          addOneNotificationDimissQuery({ item });
          break;
      }
      notificationTypeIdsForLog.push(item.notificationTypeId);
    });

    return {
      dismissIds,
      uiDeliveryIds,
      notificationTypeIds,
      notificationDeliveryIds,
      opportunityNotifications,
      notificationTypeIdsForLog,
    };
  };

  MTNotificationDismiss = (attr) => {
    const { dismissIds } = attr;
    const query = _.omit(attr, 'dismissIds', 'notificationTypeIdsForLog');
    const { logger } = this.props;

    logger.startProcess(scenarioNames.NotificationDismiss);
    NotificationResources.queueNotifications
      .updateAll(JSON.stringify(query), this.state.context, noCacheResponseRequestHeader)
      .then(() => {
        logger.endProcess(scenarioNames.NotificationDismiss, dismissIds.length.toString());
        _.each(dismissIds, id => NotificationResources.queueNotifications.eject(id));
      })
      .catch((err) => {
        this.logError(scenarioNames.NotificationDismissError, err);
      });
  };

  fetchMTNotifications = (loadScenarioName) => {
    const { logger } = this.props;

    const isInAutoLoad = loadScenarioName === scenarioNames.PreviewBarLoad;
    const loadErrorScenarioName = isInAutoLoad ?
      scenarioNames.PreviewBarLoadError : scenarioNames.NotificationLoadError;
    const renderScenarioName = isInAutoLoad ?
      scenarioNames.PreviewBarRender : scenarioNames.NotificationRender;

    if (!this.state.contextValid) {
      logger.trace(scenarioNames.InvalidContext, this.logContent(loadScenarioName));
      this.setState(_.extend({
        MTNotificationsErrLoaded: true,
      }, isInAutoLoad && {
        showTrigger: true,
      }));

      return;
    }

    if (this.state.MTNotificationsRequestState === requestStates.notStart) {
      this.setState({
        MTNotificationsRequestState: requestStates.inProgress,
        MTNotificationsErrLoaded: false,
        MTNotificationsLoaded: false,
      });

      const { context } = this.state;
      const promise = NotificationResources.queueNotifications
        .findAll({ ...context, isUINext: this.props.isUINext });

      logger.startProcess(loadScenarioName);
      promise
        .then(notificationsOrigin => !this.unmounted && this.setState((prevState) => {
          if (!isContextChanged(context, prevState.context)) {
            const {
              totalCount,
              raw: { accountNumber, previewBarDismissState, showViewAll } = {},
            } = notificationsOrigin;
            let notifications = _.map(notificationsOrigin, this.formatMTNotification);

            if (this.props.MTNotificationFilter) {
              notifications = this.props.MTNotificationFilter(notifications);
            }

            logger.endProcess(loadScenarioName, totalCount.toString());
            logger.trace(
              loadScenarioName,
              this.logContent(this.logNotificationObj({
                total: totalCount,
                notifications,
              }))
            );

            logger.startProcess(renderScenarioName);
            return _.extend({
              showTrigger: true,
              MTNotificationsRequestState: requestStates.loaded,
              MTNotificationsLoaded: true,
              MTNotifications: notifications,
              accountNumber,
              showViewAll,
            }, previewBarDismissState === previewBarDismissedValue && {
              previewBarState: previewBarStates.dismissed,
            });
          }

          return null;
        }, this.endProcessWithCount(renderScenarioName)))
        .catch((err) => {
          this.logError(loadErrorScenarioName, err);
          if (!this.unmounted) {
            this.setState({
              showTrigger: true,
              MTNotificationsErrLoaded: true,
              MTNotificationsRequestState: requestStates.notStart,
            });
          }
        });
    }
  };

  fetchWhatsNewNotification = () => {
    const {
      logger, baseHelpUrl, i18n,
    } = this.props;
    if (baseHelpUrl && this.state.whatsNewRequestState === requestStates.notStart) {
      this.setState({ whatsNewRequestState: requestStates.inProgress });

      const { context } = this.state;
      logger.startProcess(scenarioNames.WhatsNewLoad);
      const getCurrentStatePromise = NotificationResources.whatsNewDismissState.findAll(context);
      getNews({
        baseHelpUrl,
        locale: _.isFunction(i18n.getLocale) && i18n.getLocale(),
        getCurrentStatePromise,
        helpParams: this.props.helpParams,
      }).then((whatsNewOrigin = {}) =>
        !this.unmounted && this.setState((prevState) => {
          if (!isContextChanged(context, prevState.context)) {
            const hasWhatsNew = whatsNewOrigin.type && whatsNewOrigin.cacheId;

            logger.endProcess(
              scenarioNames.WhatsNewLoad,
              hasWhatsNew ? '1' : '0'
            );
            logger.trace(
              scenarioNames.WhatsNewLoad,
              this.logContent(whatsNewOrigin)
            );

            const showWhatsNew = hasWhatsNew && !prevState.whatsNewDismissed;

            return {
              showTrigger: true,
              whatsNewData: showWhatsNew ? whatsNewOrigin : {},
              whatsNewRequestState: requestStates.loaded,
              whatsNewNotifications: showWhatsNew ? [this.whatsNewNotification] : [],
            };
          }
          return null;
        }));
    }
  };

  dismissWhatsNewNotification = () => {
    const { logger } = this.props;
    const { whatsNewData, context } = this.state;
    const { type, cacheId } = whatsNewData;

    if (type && cacheId) {
      logger.trace(scenarioNames.WhatsNewDismiss, this.logContent(whatsNewData));
      NotificationResources.whatsNewDismissState
        .updateAll({ cacheId }, context, noCacheResponseRequestHeader);
      this.setState({
        whatsNewDismissed: true,
        whatsNewData: {},
        whatsNewNotifications: [],
      });
    }
  };

  hidePreviewBar = () => {
    if (this.state.previewBarState < previewBarStates.hidden) {
      this.setState({
        previewBarState: previewBarStates.hidden,
      });
    }
  };

  openQueue = () => {
    if (!this.state.MTNotificationsLoaded && !this.popover.state.open) {
      this.fetchMTNotifications(scenarioNames.NotificationLoad);
    }

    this.hidePreviewBar();
  };

  triggerClick = () => {
    if (!this.state.queueDisplayProcessLogged) {
      this.props.logger.trace(scenarioNames.TriggerClick, this.logContent());
    }

    this.openQueue();
  };

  // click view more link in preview bar
  viewMore = (e, content) => {
    e.stopPropagation();
    this.props.logger.trace(scenarioNames.PreviewBarViewMore, this.logContent(content));
    this.openQueue();
    this.popover.togglePopover(e);
  };

  closeQueue = (e) => {
    if (this.popover) {
      this.popover.dismissPopover(e);
    }
  };

  closeQueueOnAccountsSummaryPage = (e) => {
    if (this.props.isOnAccountsSummaryPage) {
      this.closeQueue(e);
    }
  };

  openDialog = (notificationId) => {
    if (this.state.dialogProps[notificationId]) {
      this.setState({
        notificationId,
        isDialogOpen: true,
      });
    }
  };

  closeDialog = () => {
    this.setState({
      isDialogOpen: false,
    });
  };

  // these two are callbacks for <Popover />
  popoverCallbackOpenQueue = () => {
    if (!this.state.isQueueOpen) {
      this.setState({ isQueueOpen: true });
    }
  };

  popoverCallbackCloseQueue = () => {
    if (this.state.isQueueOpen) {
      this.setState({ isQueueOpen: false });
    }
  };

  dismissPreviewBar = (content) => {
    this.props.logger.trace(scenarioNames.PreviewBarDismiss, this.logContent(content));
    NotificationResources.previewBarDismissState
      .updateAll({}, this.state.context, noCacheResponseRequestHeader);
    this.setState({ previewBarState: previewBarStates.dismissed });
  };

  dismissMTNotification = (notification) => {
    const dismissQuery = this.MTNotificationDismissQuery([notification]);

    this.props.logger.trace(
      scenarioNames.NotificationDismiss,
      this.logContent(dismissQuery)
    );
    this.MTNotificationDismiss(dismissQuery);
    this.setState(prevState => ({
      MTNotifications: _.reject(prevState.MTNotifications, ({ id }) => id === notification.id),
    }));
  };

  // dismiss all notifications
  dismissAll = (event) => {
    this.focusLockRef.current.focusNextElement(event.target);
    // dismiss MT notifications
    const dismissQuery = this.MTNotificationDismissQuery();
    const { dismissIds } = dismissQuery;

    if (!_.isEmpty(dismissIds)) {
      this.props.logger.trace(
        scenarioNames.NotificationDismissAll,
        this.logContent(dismissQuery)
      );
      this.MTNotificationDismiss(dismissQuery);
      this.setState({ MTNotifications: [] });
    }

    // dismiss what's new notification
    if (this.props.baseHelpUrl) {
      this.dismissWhatsNewNotification();
    }

    this.props.dismissAllExternalNotifications();
  };

  removeNotification = (notificationId) => {
    this.setState({
      unavailableNotifications: [...this.state.unavailableNotifications, notificationId],
    });
  };

  getPreviewNotifications = (orderedNotifications) => {
    /*
      Per UNQ metrics, critical/warning notifications have a higher action rate and a lower dismiss rate
      Opportunity/information notifications have a lower action rate and a higher dismiss rate
      We want to keep only critical/warning notifications in the preview bar
      With this change, the impression of the opportunity/information notifications from the preview bar is expected to disappear
      Since the preview bar click rate and the UNQ action rate are both low for opportunity/information notifications. So the impact seems acceptable
      You can find the metrics in the following link: https://lens.msftcloudes.com/#/dashboard/0b3ad1bd-b90a-4bda-9a8c-a6d33e86fe19?temp=0&newDashFromMenu=false&isSample=false&isVersion=0&isHistory=0&_g=(ws:c2f3630b-0431-46a6-9b7e-8386d5a59805)
    */
    const filteredNotifications = this.isPreviewBarFilteringEnabled
      ? _.filter(orderedNotifications, item => item.severity === 'Critical' || item.severity === 'Warning')
      : orderedNotifications;

    return getPreviewNotifications(filteredNotifications);
  };

  getShowPreviewBar = previewNotifications =>
    this.props.shouldShowPreviewBar &&
    this.state.previewBarState < previewBarStates.hidden &&
    this.state.MTNotificationsRequestState === requestStates.loaded &&
    previewNotifications.length > 0;

  render() {
    const { totalCount, orderedNotifications } = this;
    // Below two lines are for reducing the calculation
    const previewNotifications = this.getPreviewNotifications(orderedNotifications);
    const showPreviewBar = this.getShowPreviewBar(previewNotifications);

    let previewBarProps;

    if (showPreviewBar) {
      const previewNotificationLogData = previewNotifications.map(notification => _.pick(notification, notificationLogKeys));

      previewBarProps = {
        items: getPreviewItems(previewNotifications),
        notificationCount: totalCount,
        viewMoreText: this.props.i18n.getString(_TL_('and more')),
        viewMoreAction: e => this.viewMore(e, previewNotificationLogData),
        dismissAction: () => this.dismissPreviewBar(previewNotificationLogData),
        i18n: this.props.i18n,
      };
    }

    const {
      PreviewBar,
    } = this.props;

    const {
      cid, aid, uid, campaignId,
    } = this.props.context;
    const propContextKey = `cid:${cid},aid:${aid},uid:${uid},cmpid:${campaignId}`;

    const trigger = (
      <Trigger
        active={totalCount > 0}
        action={this.triggerClick}
        isUINext={this.props.isUINext}
        text={this.props.i18n.getString(_TL_('Notifications'))}
        isQueueOpen={this.state.isQueueOpen}
        disableKeyboardNav={this.props.forNewGlobalMenu}
        triggerButtonRole={this.props.triggerButtonRole}
      />
    );

    return (
      <div className="unified-notification-queue">
        {this.state.showTrigger || this.state.showTriggerFromExternal || this.props.isUINext ? (
          <div>
            <Popover
              key={propContextKey}
              trigger="click"
              target={trigger}
              placement="bottom"
              customKeepOpen={customKeepOpen}
              ref={(popover) => { this.popover = popover; }}
              onOpen={this.popoverCallbackOpenQueue}
              onDismiss={this.popoverCallbackCloseQueue}
              keepTargetPropagation
            >
              <FocusLock
                onExit={() => this.popover.togglePopover({ type: 'click' })}
                focusFirstItemOnMount
                ref={this.focusLockRef}
              >
                <NotificationQueue
                  header={this.header}
                  notifications={orderedNotifications}
                  footer={this.footer}
                  emptyText={this.emptyText}
                  showLoading={
                    !(this.state.MTNotificationsLoaded || this.state.MTNotificationsErrLoaded)
                  }
                  actionCallback={this.actionCallback}
                  closeQueue={this.closeQueue}
                  logger={this.props.logger}
                  logContent={this.logContent}
                  isUINext={this.props.isUINext}
                  i18n={this.props.i18n}
                />
                <Dialog
                  hidden={!this.state.isDialogOpen}
                  onDismiss={this.closeDialog}
                  minWidth={500}
                  dialogContentProps={{
                    type: DialogType.normal,
                    title: _.result(this.state.dialogProps[this.state.notificationId], 'dialogTitle', ''),
                    showCloseButton: true,
                    subText: _.result(this.state.dialogProps[this.state.notificationId], 'dialogText', ''),
                  }}
                  modalProps={{
                    isBlocking: true,
                  }}
                >
                  <DialogFooter>
                    <PrimaryButton
                      autoFocus
                      className="dialog-confirm"
                      onClick={() => {
                        this.closeDialog();
                        if (this.state.dialogProps[this.state.notificationId] &&
                          _.isFunction(this.state.dialogProps[this.state.notificationId].dialogAction)) {
                          this.state.dialogProps[this.state.notificationId].dialogAction(() => this.removeNotification(this.state.notificationId));
                        }
                      }}
                      text={_.result(this.state.dialogProps[this.state.notificationId], 'dialogConfirm', '')}
                    />
                    <DefaultButton
                      className="dialog-cancel"
                      onClick={this.closeDialog}
                      text={_.result(this.state.dialogProps[this.state.notificationId], 'dialogCancel', '')}
                    />
                  </DialogFooter>
                </Dialog>
              </FocusLock>
            </Popover>
            {showPreviewBar ? (<PreviewBar {...previewBarProps} />) : null}
          </div>
        ) : null}
      </div>
    );
  }
}

UnifiedNotificationQueue.propTypes = {
  PreviewBar: PropTypes.node,
  shouldShowPreviewBar: PropTypes.bool.isRequired,
  isUINext: PropTypes.bool.isRequired,
  externalNotifications: PropTypes.arrayOf(PropTypes.shape({
    severity: PropTypes.oneOf(_.keys(severityOrder)),
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    title: PropTypes.string,
    message: PropTypes.string,
    actionList: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      href: PropTypes.string,
      action: PropTypes.func,
      extraClassNames: PropTypes.arrayOf(PropTypes.string),
      extraAttrs: PropTypes.objectOf(PropTypes.string),
      callbackType: PropTypes.string,
      isJumpout: PropTypes.bool,
    })),
    dismissAction: PropTypes.func,
  })),
  dismissAllExternalNotifications: PropTypes.func,
  context: PropTypes.shape({
    cid: PropTypes.number,
    aid: PropTypes.number,
    uid: PropTypes.number,
    campaignId: PropTypes.number,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    getLocale: PropTypes.func,
  }).isRequired,
  ccBaseUrl: PropTypes.string.isRequired,
  logger: PropTypes.shape({
    mark: PropTypes.func,
    startProcess: PropTypes.func,
    endProcess: PropTypes.func,
    trace: PropTypes.func,
    error: PropTypes.func,
  }),
  generateViewAllLink: PropTypes.func,
  goTargetPage: PropTypes.func,
  getPathBySiteMapId: PropTypes.func,
  isOnAccountsSummaryPage: PropTypes.bool,
  baseHelpUrl: PropTypes.string.isRequired,
  whatsNewHelpQuery: PropTypes.string,
  helpParams: PropTypes.shape({
    project: PropTypes.string,
    language: PropTypes.string,
    asRow: PropTypes.number,
    query: PropTypes.string,
  }),
  MTNotificationFilter: PropTypes.func,
  permissions: PropTypes.shape({}),
  setIsActive: PropTypes.func,
  forNewGlobalMenu: PropTypes.bool,
  triggerButtonRole: PropTypes.string,
};

UnifiedNotificationQueue.defaultProps = {
  externalNotifications: [],
  dismissAllExternalNotifications: _.noop,
  logger: {
    mark: _.noop,
    startProcess: _.noop,
    endProcess: _.noop,
    trace: _.noop,
    error: _.noop,
  },
  generateViewAllLink: _.noop,
  goTargetPage: undefined,
  getPathBySiteMapId: undefined,
  isOnAccountsSummaryPage: false,
  whatsNewHelpQuery,
  helpParams: undefined,
  MTNotificationFilter: undefined,
  permissions: {},
  setIsActive: _.noop,
  forNewGlobalMenu: false,
  triggerButtonRole: '',
  PreviewBar: defaultPreviewBar,
};
